import angular from "angular";

import { Ajax, ApiValidationError, Onboarding, Role, Translations } from "@salaxy/core";

import { OnboardingService, SessionService, WizardService, WizardStep, UiHelpers } from "../../../services";
import { WizardController } from "../../bases/WizardController";

/**
 * Wizard for creating a new Palkkaus.fi-account for a company.
 */
export class CompanyOnboardingController extends WizardController {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["$scope", "WizardService", "OnboardingService", "SessionService", "AjaxNg1", "$location", "data", "UiHelpers"];

  /**
   * Field to expose forms validity state.
   */
  public formDataValidity: boolean;

  /** If true, step is proceeding */
  public isStepProceeding = false;

  constructor(
    $scope: angular.IScope,
    wizardService: WizardService,
    private onboardingService: OnboardingService,
    private sessionService: SessionService,
    private ajax: Ajax,
    private $location: angular.ILocationService,
    data: any,
    private uiHelpers: UiHelpers,
  ) {
    super($scope, wizardService, data);
  }

  /**
   * Implement IController
   */
  public $onInit() {

    this.wizardService.setSteps(this.getWizardSteps());
    if (this.model && this.model.signature.isProcura) {
      this.wizardService.activeStepNumber = 3;
    } else {
      this.wizardService.activeStepNumber = 1;
    }
  }

  /** Company wizard configuration */
  public getWizardSteps(): WizardStep[] {
    const steps: WizardStep[] = [];
    steps.push(
      {
        title: "SALAXY.NG1.CompanyOnboardingComponent.step1.title",
        intro: "SALAXY.NG1.CompanyOnboardingComponent.step1.intro",
        active: true,
        view: "salaxy-components/modals/onboarding/company/wizard_index.html",
        buttonsView: "salaxy-components/modals/onboarding/company/firstPageButtons.html",
      });
    if (this.showCompanySelection) {
      steps.push(
        {
          title: "SALAXY.NG1.CompanyOnboardingComponent.stepSuomiFi.title",
          intro: "SALAXY.NG1.CompanyOnboardingComponent.stepSuomiFi.intro",
          view: "salaxy-components/modals/onboarding/company/suomifi.html",
          buttonsView: "salaxy-components/modals/onboarding/company/suomifiButtons.html",
        });
    }
    steps.push(
      {
        title: "SALAXY.NG1.CompanyOnboardingComponent.stepCompanyInfo.title",
        intro: "SALAXY.NG1.CompanyOnboardingComponent.stepCompanyInfo.intro",
        view: "salaxy-components/modals/onboarding/company/company.html",
        buttonsView: "salaxy-components/modals/onboarding/company/defaultWizardButtons.html",
      },
      /*
      {
        title: "SALAXY.NG1.CompanyOnboardingComponent.stepCompanyLegal.title",
        view: "salaxy-components/modals/onboarding/company/company-legal.html",
        buttonsView: "salaxy-components/modals/onboarding/company/defaultWizardButtons.html",
      },
      */
      {
        title: "SALAXY.NG1.CompanyOnboardingComponent.stepContract.title",
        view: "salaxy-components/modals/onboarding/company/contract.html",
        buttonsView: "salaxy-components/modals/onboarding/company/contractButtons.html",
      },
    );
    return steps;
  }

  /**
   * Navigates to the next step if possible and saves the data.
   */
  public goNext(): Promise<boolean> {
    if (this.isStepProceeding) {
      return Promise.resolve(false);
    }
    this.isStepProceeding = true;
    return this.save().then(() => {
      super.goNext();
      this.isStepProceeding = false;
      return true;
    }).catch((reason) => {
      this.isStepProceeding = false;
      return false;
    });
  }

  /** Returns true if user can go forward in wizard  */
  public get canGoNext(): boolean {
    if (this.steps.length > this.step) {
      if (this.steps[this.step] && !this.steps[this.step].disabled) {
        if (this.formDataValidity) {
          /* if (this.model.validation.isOfficialIdUnique == true){ */
          return true;
          /* } */
        }
      }
    }
    return false;
  }

  /**
   * Navigates to the previous step if possible and saves the data.
   */
  public goPrevious() {
    if (this.isStepProceeding) {
      return;
    }
    this.isStepProceeding = true;
    this.save().then(() => {
      super.goPrevious();
      this.isStepProceeding = false;
    })
      .catch((reason) => {
        this.isStepProceeding = false;
      });
  }

  /**
   * Saves the data to server
   */
  public save(): Promise<Onboarding> {
    this.model.signature.email = this.model.person.contact.email;
    this.model.signature.telephone = this.model.person.contact.telephone;
    this.model.signature.personName = (this.model.person.firstName + " " + this.model.person.lastName).trim();
    return this.onboardingService.save();
  }

  /**
   * Returns signing url.
   */
  public get vismaSignUrl(): string {
    return this.onboardingService.getDigitalSignUrl();
  }

  /**
   * The onboarding model is provided by the onboarding service.
   */
  public get model(): Onboarding {
    return this.onboardingService.model;
  }

  /** IF true, the Suomi.fi company selection is used. */
  public get showCompanySelection(): boolean {

    const isAccountant = this.model &&
    this.model.company &&
     this.model.company.roles &&
     this.model.company.roles.some( (r) => ( r === Role.Accountant || r === Role.AccountantCandidate));

    return isAccountant;
  }

  /** Returns the PDF preview address for the authorization pdf. */
  public getPdfPreviewAddress() {
    return this.onboardingService.getPdfPreviewAddress();
  }

  /**
   * Existing company alert.
   */
  public get existingCompanyAlert(): boolean {
    const currentAccountId = this.sessionService.session?.currentAccount?.id;
    // existing account in the system and is different than current account
    return this.model?.company?.resolvedId &&
           this.model.company.resolvedId != currentAccountId;
  }

  /** Go to suomi fi ypa */
  public goToSuomifi() {

    const rawUrl = this.$location.url();
    let baseUrl = this.$location.absUrl();
    if (rawUrl.length > 1) {
      baseUrl = baseUrl.substring(0, baseUrl.indexOf(rawUrl));
    }
    baseUrl = baseUrl.replace(/\/$/, "").trim();

    const accessToken = this.ajax.getCurrentToken();
    const token = accessToken ? `${encodeURIComponent(accessToken)}` : "";
    const urlParam = ( baseUrl.indexOf("&") >= 0 || baseUrl.indexOf("?") >= 0) ? "&url=" : "";
    const cancelUrl = `${baseUrl}${urlParam}/onboarding/company/${this.model.id}${token ? "&ob_token=" + token : ""}`;
    const successUrl = `${baseUrl}${urlParam}/onboarding/company/${this.model.id}${token ? "&ob_token=" + token : ""}`;
    this.save().then(() => {
      window.location.assign(`${this.ajax.getServerAddress()}/Onboarding/CompanySelection?OnboardingId=${encodeURIComponent(this.model.id)}&Language=${encodeURIComponent(Translations.getLanguage())}&SuccessUrl=${encodeURIComponent(successUrl)}&CancelUrl=${encodeURIComponent(cancelUrl)}${token ? "&access_token=" + token : ""}`);
    });
  }

  /**
   * Launches the wizard.
   * @param id Optional onboarding id.
   */
  public launch(id: string = null): Promise<any> {
    return this.onboardingService.launchCompanyOnboarding(id);
  }

  /**
   * Sets the company's business id, clears the YTJ search results fetches other company info's via YTJ search and saves
   * @param businessId The business id that is set as the company's business id
   */
  public chooseBusinessId(businessId) {
    this.model.company.businessId = businessId;
    this.model.company.ytjSearch = null;
    this.searchYtj();
  }

  /**
   * Sets the DoYtjUpdate flag to true and saves.
   * @param searchType - If true, sets the business ID to null
   * Use this option to force a YTJ name search, even if there already is something written in business ID field.
   */
  public searchYtj(searchType?: "businessId" | "name") {
    // Clear company's type and address
    this.model.company.companyType = null;
    this.model.company.contact.street = null;
    this.model.company.contact.city = null;
    this.model.company.contact.postalCode = null;
    // Clear previous YTJ search results
    this.model.company.ytjSearch = null;
    // Prepare for YTJ search
    this.model.company.doYtjUpdate = true;
    if (searchType === "name") {
      this.model.company.businessId = null;
    }
    if (searchType === "businessId") {

      this.model.company.name = null;
    }

    this.save();
  }

  /**
   * Returns validation error for key if exists.
   * @param key - Validation error key.
   */
  public getValidationError(key: string): ApiValidationError {
    if (this.model && this.model.validation) {
      return this.model.validation.errors.find((x) => x.key === key);
    }
    return null;
  }

  /** Opens wizard as modal dialog */
  public openSigningWizardDialog(onBoardingId = null): Promise<any> {
    const skin = this.sessionService.getCurrentCss();
    const origin = this.sessionService.getOrigin();
    const url =  origin + (skin ?
      `popup.html#settings/signing?skin=${skin}&lang=${Translations.getLanguage()}&id=${this.onboardingService.model?.id ?? ""}#access_token=${this.ajax.getCurrentToken()}` :
      `popup.html#settings/signing?lang=${Translations.getLanguage()}&id=${this.onboardingService.model?.id ?? ""}#access_token=${this.ajax.getCurrentToken()}`);
    return this.uiHelpers.showExternalDialog(onBoardingId, url , { id: onBoardingId, origin }, {width: 1080, height: 800}).then( (result) => {
      if (result?.action != "cancel") {
        return this.checkSession();
      } else {
        return;
      }
    });
  }

  /** Launches company wizard. */
  public launchSigningWizard() {
    const data =  this.readTokenFromUrl(this.$location.absUrl(), "data=");
    const id = JSON.parse(atob(decodeURIComponent(data)))?.id;
    const origin = JSON.parse(atob(decodeURIComponent(data)))?.origin;
    return this.onboardingService.getOnboardingData(id).then( () => {
      const skin = this.sessionService.getCurrentCss();
      this.onboardingService.model.ui.successUrl = origin + (skin ?
        `popup.html#settings/signing-success?skin=${skin}&lang=${Translations.getLanguage()}&id=${this.onboardingService.model?.id ?? ""}#access_token=${this.ajax.getCurrentToken()}` :
        `popup.html#settings/signing-success?lang=${Translations.getLanguage()}&id=${this.onboardingService.model?.id ?? ""}#access_token=${this.ajax.getCurrentToken()}`);
      this.onboardingService.model.ui.cancelUrl = this.onboardingService.model.ui.successUrl.replace("/signing-success?", "/signing-cancel?");
      return this.onboardingService.save().then( () => {
        return this.onboardingService.launchCompanyOnboarding(this.onboardingService.model.id);
      });
    });
  }

  /** Reloads the current session*/
  public checkSession(): Promise<any> {
    return this.sessionService.checkSession();
  }

  private readTokenFromUrl(url: string, key: string): string {
    // shoud use regex...
    const start = url.toLowerCase().indexOf(key);
    if (start >= 0) {
      const end = url.indexOf("&", start);
      return end >= 0
        ? url.substring(start + key.length, end)
        : url.substring(start + key.length);
    }
    return null;
  }

}
